/**
 * Which part of a multi-tab canvas a selection-bound container belongs to.
 *
 * A canvas has one form state and one submit footer. A selection-bound detail
 * container (a card that edits the row picked in a grid) only governs the tab
 * that holds it; other tabs keep their ordinary form and footer.
 */

export interface ScopeNode {
  id?: string | null;
  parent_element_id?: string | null;
  parent_panel_id?: string | null;
}

/** The active panel id of each TABS container, keyed by the container's element id. */
export type ActivePanels = Record<string, string>;

/**
 * True when every tab panel on the path from the element up to the root is the
 * one currently shown. A container whose active panel is not known yet counts as
 * active, so nothing changes until a tab control has reported itself.
 */
export function isInActiveScope(
  elementId: string,
  elements: ScopeNode[],
  activePanels: ActivePanels,
): boolean {
  const byId = new Map<string, ScopeNode>();
  for (const element of elements) {
    if (element.id) byId.set(String(element.id), element);
  }
  const visited = new Set<string>();
  let node = byId.get(elementId);
  while (node && node.parent_element_id) {
    const parentId = String(node.parent_element_id);
    if (visited.has(parentId)) return true;
    visited.add(parentId);
    const panelId = node.parent_panel_id ? String(node.parent_panel_id) : null;
    const active = activePanels[parentId];
    if (panelId && active !== undefined && active !== panelId) return false;
    node = byId.get(parentId);
  }
  return true;
}

/** True when the element is the container itself or sits anywhere below it. */
export function isWithinContainer(
  elementId: string,
  containerId: string,
  elements: ScopeNode[],
): boolean {
  const byId = new Map<string, ScopeNode>();
  for (const element of elements) {
    if (element.id) byId.set(String(element.id), element);
  }
  const visited = new Set<string>();
  let currentId: string | null = elementId;
  while (currentId && !visited.has(currentId)) {
    if (currentId === containerId) return true;
    visited.add(currentId);
    const parent: string | null | undefined =
      byId.get(currentId)?.parent_element_id;
    currentId = parent ? String(parent) : null;
  }
  return false;
}

/** A copy of the record without the given keys. */
export function omitKeys<T>(
  record: Record<string, T>,
  keys: ReadonlySet<string>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(record).filter(([key]) => !keys.has(key)),
  );
}

/** A copy of the record holding only the given keys. */
export function pickKeys<T>(
  record: Record<string, T>,
  keys: ReadonlySet<string>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(record).filter(([key]) => keys.has(key)),
  );
}
